import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetLoanApplicationByTask, useVerifyCollateral, useSubmitCollateralVerification, cancelLoanApplication } from '../../../services/loanService';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas';
import { UseQueryOptions } from '@tanstack/react-query';
import { CollateralVerification } from '../../../services/schemas/loanServiceSchemas/collateralVerification';
import { CollateralVerificationSubmission } from '../../../services/schemas/loanServiceSchemas/collateralVerificationSubmission';
import { CollateralVerificationStatus } from '../../../services/schemas/loanServiceSchemas/collateralVerificationStatus';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import './CollateralVerificationDetails.css';
import CollateralVehicleDocuments from './CollateralVehicleDocuments';

/** Photos per collateral: at least six (business presentation, 23 Oct 2026); the API keeps six. */
const MAX_PHOTOS = 6;

// Define types for our component
interface CollateralDocument {
  id: string;
  type: string;
  fileName: string;
  uploadDate: Date;
  fileUrl?: string;
  file?: File;
}

// Define document types enum for better type safety
enum DocumentType {
  COLLATERAL_REPORT = 'COLLATERAL_REPORT',
  COLLATERAL_CHECKLIST = 'COLLATERAL_CHECKLIST'
}

interface CollateralPhoto {
  id: string;
  fileName: string;
  uploadDate: Date;
  imageUrl: string;
  file?: File;
  isExisting: boolean;
}

interface Collateral {
  index: number;
  type: string;
  description: string;
  value: number;
  documents: CollateralDocument[];
  photos: CollateralPhoto[];
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
  notes: string;
  /** A vehicle (type, make or registration number captured): its registration document is needed. */
  isVehicle?: boolean;
  /** A vehicle's insurance, captured on the application (finding #10) */
  insured?: boolean;
  insurer?: string;
  insurancePolicyNumber?: string;
  verificationStatus?: CollateralVerificationStatus;  // From API
  verificationNotes?: string;  // From API
  collateralReportDocumentKey?: string;  // From API
  collateralChecklistDocumentKey?: string;  // From API
  photoDocumentKey1?: string;  // From API
  photoDocumentKey2?: string;  // From API
  photoDocumentKey3?: string;  // From API
  photoDocumentKey5?: string;
  photoDocumentKey6?: string;
  photoDocumentKey4?: string;  // From API
}

interface CollateralFormState {
  applicationId: string | undefined;
  collaterals: Collateral[];
  selectedCollateralIndex: number | null;
  notes: string;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
}

const CollateralVerificationDetails: React.FC = () => {
  // Get taskId from URL params
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  // Recorded as who submitted the outcome, instead of the login id.
  const user = useSelector((state: RootState) => state.auth.user);

  console.log('Current taskId:', taskId); // Add logging to debug

  // States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [missingDocTypes, setMissingDocTypes] = useState<string[]>([]);
  // Whether the application already holds the vehicle registration document (finding #16)
  const [hasVehicleRegistration, setHasVehicleRegistration] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [loanApplicationId, setLoanApplicationId] = useState<string | undefined>();
  // Add loading states for different operations
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isVerifyingCollateral, setIsVerifyingCollateral] = useState<boolean>(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // Form state
  const [formState, setFormState] = useState<CollateralFormState>({
    applicationId: undefined,
    collaterals: [],
    selectedCollateralIndex: null,
    notes: '',
    status: 'PENDING'
  });

  // First, fetch loan application by task ID
  const {
    data: loanApplication,
    isLoading: isLoadingApplication,
    error: applicationError,
    refetch: refetchLoanApplication
  } = useGetLoanApplicationByTask(
    taskId || '', 
    {
      query: {
        enabled: Boolean(taskId),
        onSuccess: (data: LoanApplication) => {
          console.log('Loan application fetched by task:', data);
          if (data?.id) {
            console.log('Setting loan application ID:', data.id);
            setLoanApplicationId(data.id);
            // Only set the form state if we have a valid ID
            if (typeof data.id === 'string') {
              setFormState(prev => ({
                ...prev,
                applicationId: data.id
              }));
            }
          } else {
            setError('The application data is missing an ID. Please try refreshing the page.');
          }
        },
        onError: (error: any) => {
          console.error('Error fetching loan application:', error);
          // Extract detailed error information
          const errorDetails = error?.response?.data?.message || error?.message || 'Unknown error';
          const errorMsg = `Failed to load application: ${errorDetails}`;
          
          setError(errorMsg);
          setIsLoading(false);
          toast.error(errorMsg);
        }
      } as Partial<UseQueryOptions<LoanApplication, Error>>
    }
  );

  // Handle loan application data changes
  useEffect(() => {
    if (loanApplication?.id && typeof loanApplication.id === 'string') {
      console.log('Setting loan application data:', loanApplication);
      setLoanApplicationId(loanApplication.id);
      
      // Map collaterals from loan application
      const mappedCollaterals: Collateral[] = loanApplication.collaterals?.map((collateral: any, index: number) => {
        // Create initial documents array based on existing document keys
        const documents: CollateralDocument[] = [];
        if (collateral.collateralReportDocumentKey) {
          documents.push({
            id: collateral.collateralReportDocumentKey,
            type: DocumentType.COLLATERAL_REPORT,
            fileName: 'Collateral Report',
            uploadDate: new Date(),
            fileUrl: `/loan/api/v1/documents/${collateral.collateralReportDocumentKey}`
          });
        }
        if (collateral.collateralChecklistDocumentKey) {
          documents.push({
            id: collateral.collateralChecklistDocumentKey,
            type: DocumentType.COLLATERAL_CHECKLIST,
            fileName: 'Collateral Checklist',
            uploadDate: new Date(),
            fileUrl: `/loan/api/v1/documents/${collateral.collateralChecklistDocumentKey}`
          });
        }

        // Create initial photos array based on existing photo keys with proper URLs
        const photos: CollateralPhoto[] = [];
        [
          collateral.photoDocumentKey1,
          collateral.photoDocumentKey2,
          collateral.photoDocumentKey3,
          collateral.photoDocumentKey4,
          collateral.photoDocumentKey5,
          collateral.photoDocumentKey6
        ].filter(Boolean).forEach((photoKey, photoIndex) => {
          photos.push({
            id: photoKey,
            fileName: `Photo ${photoIndex + 1}`,
            uploadDate: new Date(),
            imageUrl: `/loan/api/v1/documents/${photoKey}`,
            isExisting: true // Flag to identify existing photos
          });
        });

        return {
          index,
          type: collateral.make || 'UNSPECIFIED',
          isVehicle: collateral.collateralType === 'VEHICLE' || !!collateral.registrationNumber || !!collateral.make,
          insured: collateral.insured,
          insurer: collateral.insurer,
          insurancePolicyNumber: collateral.insurancePolicyNumber,
          description: collateral.assetDescription || '',
          value: collateral.value || 0,
          documents,
          photos,
          status: collateral.verificationStatus === 'APPROVE' ? 'VERIFIED' 
                 : collateral.verificationStatus === 'REJECT' ? 'REJECTED' 
                 : 'PENDING',
          notes: collateral.verificationNotes || collateral.otherDetails || '',
          verificationStatus: collateral.verificationStatus,
          verificationNotes: collateral.verificationNotes,
          collateralReportDocumentKey: collateral.collateralReportDocumentKey,
          collateralChecklistDocumentKey: collateral.collateralChecklistDocumentKey,
          photoDocumentKey1: collateral.photoDocumentKey1,
          photoDocumentKey2: collateral.photoDocumentKey2,
          photoDocumentKey3: collateral.photoDocumentKey3,
          photoDocumentKey4: collateral.photoDocumentKey4,
          photoDocumentKey5: collateral.photoDocumentKey5,
          photoDocumentKey6: collateral.photoDocumentKey6
        };
      }) || [];

      console.log('Mapped collaterals:', mappedCollaterals);

      setFormState(prev => ({
        ...prev,
        applicationId: loanApplication.id,
        collaterals: mappedCollaterals,
        selectedCollateralIndex: mappedCollaterals.length > 0 ? 0 : null
      }));
    }
  }, [loanApplication]);

  // Handle application errors
  useEffect(() => {
    if (applicationError) {
      console.error('Error fetching loan application:', applicationError);
      // Extract detailed error information
      const errorMsg = applicationError instanceof Error 
        ? applicationError.message 
        : typeof applicationError === 'object' && applicationError !== null && 'message' in applicationError
        ? String((applicationError as any).message)
        : 'Unknown error occurred while loading application';
      
      setError(`Failed to load application: ${errorMsg}`);
      setIsLoading(false);
      toast.error(`Failed to load application: ${errorMsg}`);
    }
  }, [applicationError]);

  // Add new state for drag-and-drop functionality
  const [isDraggingDocument, setIsDraggingDocument] = useState<boolean>(false);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Use effects
  useEffect(() => {
    if (isLoadingApplication) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isLoadingApplication]);

  // Define required documents based on collateral type
  const getRequiredDocuments = useCallback(() => {
    const selectedCollateral = formState.selectedCollateralIndex !== null
      ? formState.collaterals[formState.selectedCollateralIndex]
      : null;

    if (!selectedCollateral) return [];

    // No collateral report or verification checklist any more (business test, 22 Oct 2026): the
    // security documents below are the application's own, uploaded once on Upload Documents.
    return [] as { type: string; label: string; description: string; required: boolean }[];
  }, [formState.selectedCollateralIndex, formState.collaterals]);

  // Check if required documents are uploaded and update missing documents
  const checkRequiredDocuments = useCallback(() => {
    if (formState.selectedCollateralIndex === null) return [];

    const selectedCollateral = formState.collaterals[formState.selectedCollateralIndex];
    const uploadedDocTypes = selectedCollateral.documents.map(doc => doc.type);

    const missing = getRequiredDocuments()
      .filter(doc => doc.required)
      .filter(doc => !uploadedDocTypes.includes(doc.type))
      .map(doc => doc.type);

    return missing;
  }, [formState.selectedCollateralIndex, formState.collaterals, getRequiredDocuments]);

  // Update missing documents when selected collateral or documents change
  useEffect(() => {
    const missing = checkRequiredDocuments();
    setMissingDocTypes(missing);
  }, [formState.selectedCollateralIndex, formState.collaterals, checkRequiredDocuments]);

  // Handlers
  const handleCollateralChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(e.target.value);
    setFormState(prev => ({
      ...prev,
      selectedCollateralIndex: index
    }));
  };

  // Enhanced handleFileChange with better error handling
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, documentType: string) => {
    try {
      if (e.target.files && e.target.files.length > 0) {
        setUploadError(null);
        setOperationError(null);
        const file = e.target.files[0];

        // File validation
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          const errorMsg = `File type not supported for "${file.name}". Please upload PDF, JPG, or PNG files.`;
          setUploadError(errorMsg);
          setOperationError(errorMsg);
          return;
        }

        if (file.size > 100 * 1024 * 1024) { // 100MB limit
          const errorMsg = `File size exceeds 100MB limit: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB)`;
          setUploadError(errorMsg);
          setOperationError(errorMsg);
          return;
        }

        // Simulate upload progress
        setUploadProgress(0);
        const interval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev === null) return 0;
            if (prev >= 100) {
              clearInterval(interval);
              setTimeout(() => setUploadProgress(null), 500);
              return 100;
            }
            return prev + 10;
          });
        }, 200);

        // Check if this document type already exists
        if (formState.selectedCollateralIndex !== null) {
          const index = formState.selectedCollateralIndex; // Store in a const to avoid null check issues
          const updatedCollaterals = [...formState.collaterals];
          const selectedCollateral = updatedCollaterals[index];
          const existingDocIndex = selectedCollateral.documents.findIndex(doc => doc.type === documentType);

          setTimeout(() => {
            try {
              if (existingDocIndex >= 0) {
                // Replace existing document
                const newDocuments = [...selectedCollateral.documents];
                newDocuments[existingDocIndex] = {
                  id: newDocuments[existingDocIndex].id,
                  type: documentType,
                  fileName: file.name,
                  uploadDate: new Date(),
                  fileUrl: URL.createObjectURL(file),
                  file
                };

                updatedCollaterals[index] = {
                  ...selectedCollateral,
                  documents: newDocuments
                };
              } else {
                // Add new document
                const newDocument: CollateralDocument = {
                  id: Date.now().toString(),
                  type: documentType,
                  fileName: file.name,
                  uploadDate: new Date(),
                  fileUrl: URL.createObjectURL(file),
                  file
                };

                updatedCollaterals[index] = {
                  ...selectedCollateral,
                  documents: [...selectedCollateral.documents, newDocument]
                };
              }

              setFormState(prev => ({
                ...prev,
                collaterals: updatedCollaterals
              }));

              // Clear validation errors after successful upload
              if (validationErrors.documents) {
                setValidationErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.documents;
                  return newErrors;
                });
              }

              toast.success(`Document "${file.name}" uploaded successfully`);
            } catch (uploadError) {
              console.error('Error processing uploaded file:', uploadError);
              const errorMsg = `Error processing file: ${file.name}. Please try again.`;
              setUploadError(errorMsg);
              setOperationError(errorMsg);
              clearInterval(interval);
              setUploadProgress(null);
            }
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMsg = 'An error occurred while uploading the file. Please try again.';
      setUploadError(errorMsg);
      setOperationError(errorMsg);
      setUploadProgress(null);
    }
  };

  // Document drag and drop handlers
  const handleDocumentDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingDocument(true);
  };

  const handleDocumentDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingDocument(false);
  };

  const handleDocumentDrop = (e: React.DragEvent<HTMLDivElement>, type: string) => {
    e.preventDefault();
    setIsDraggingDocument(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange({ target: { files: e.dataTransfer.files } } as React.ChangeEvent<HTMLInputElement>, type);
    }
  };

  // Enhanced photo upload handler with drag-and-drop support
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    if (!file) return;

    try {
      // Create a URL for preview
      const imageUrl = URL.createObjectURL(file);

      if (formState.selectedCollateralIndex !== null) {
        const updatedCollaterals = [...formState.collaterals];
        const currentPhotos = updatedCollaterals[formState.selectedCollateralIndex].photos;

        if (currentPhotos.length >= MAX_PHOTOS) {
          toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
          return;
        }

        const newPhoto: CollateralPhoto = {
          id: Math.random().toString(36).substring(7),
          fileName: file.name,
          uploadDate: new Date(),
          imageUrl,
          file,
          isExisting: false
        };

        updatedCollaterals[formState.selectedCollateralIndex] = {
          ...updatedCollaterals[formState.selectedCollateralIndex],
          photos: [...currentPhotos, newPhoto]
        };

        setFormState(prev => ({
          ...prev,
          collaterals: updatedCollaterals
        }));

        toast.success('Photo uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadError('Failed to upload photo');
      toast.error('Failed to upload photo');
    }
  };

  // Photo drag and drop handlers
  const handlePhotoDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingPhoto(true);
  };

  const handlePhotoDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingPhoto(false);
  };

  const handlePhotoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingPhoto(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handlePhotoUpload({ target: { files: e.dataTransfer.files } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  // Update the photo preview handling
  const handlePreviewPhoto = (photo: CollateralPhoto) => {
    // Create a loading indicator
    const loadingModal = document.createElement('div');
    loadingModal.style.position = 'fixed';
    loadingModal.style.top = '0';
    loadingModal.style.left = '0';
    loadingModal.style.width = '100%';
    loadingModal.style.height = '100%';
    loadingModal.style.backgroundColor = 'rgba(0,0,0,0.8)';
    loadingModal.style.display = 'flex';
    loadingModal.style.alignItems = 'center';
    loadingModal.style.justifyContent = 'center';
    loadingModal.style.zIndex = '9999';
    loadingModal.innerHTML = `
      <div class="text-white text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading photo...</p>
      </div>
    `;
    document.body.appendChild(loadingModal);

    // Create an image element
    const img = new Image();
    img.style.maxWidth = '90%';
    img.style.maxHeight = '90%';
    img.style.objectFit = 'contain';

    // Handle image load
    img.onload = () => {
      // Remove loading indicator
      document.body.removeChild(loadingModal);

      // Create preview modal
      const modal = document.createElement('div');
      modal.style.position = 'fixed';
      modal.style.top = '0';
      modal.style.left = '0';
      modal.style.width = '100%';
      modal.style.height = '100%';
      modal.style.backgroundColor = 'rgba(0,0,0,0.8)';
      modal.style.display = 'flex';
      modal.style.alignItems = 'center';
      modal.style.justifyContent = 'center';
      modal.style.zIndex = '9999';
      modal.style.cursor = 'pointer';

      // Add close button
      const closeButton = document.createElement('button');
      closeButton.innerHTML = '×';
      closeButton.style.position = 'absolute';
      closeButton.style.top = '20px';
      closeButton.style.right = '20px';
      closeButton.style.backgroundColor = 'transparent';
      closeButton.style.border = 'none';
      closeButton.style.color = 'white';
      closeButton.style.fontSize = '40px';
      closeButton.style.cursor = 'pointer';
      modal.appendChild(closeButton);

      // Add image to modal
      modal.appendChild(img);

      // Add click handlers to close modal
      [modal, closeButton].forEach(element => {
        element.addEventListener('click', () => {
          document.body.removeChild(modal);
        });
      });

      document.body.appendChild(modal);
    };

    // Handle image error
    img.onerror = () => {
      document.body.removeChild(loadingModal);
      toast.error('Failed to load photo preview');
    };

    // Set image source
    img.src = photo.imageUrl;
  };

  const handleDeleteDocument = (documentId: string) => {
    if (formState.selectedCollateralIndex !== null) {
      const updatedCollaterals = [...formState.collaterals];
      updatedCollaterals[formState.selectedCollateralIndex] = {
        ...updatedCollaterals[formState.selectedCollateralIndex],
        documents: updatedCollaterals[formState.selectedCollateralIndex].documents.filter(
          doc => doc.id !== documentId
        )
      };

      setFormState(prev => ({
        ...prev,
        collaterals: updatedCollaterals
      }));

      toast.info('Document removed');
    }
  };

  const handleDeletePhoto = (photoId: string) => {
    if (formState.selectedCollateralIndex !== null) {
      const updatedCollaterals = [...formState.collaterals];
      updatedCollaterals[formState.selectedCollateralIndex] = {
        ...updatedCollaterals[formState.selectedCollateralIndex],
        photos: updatedCollaterals[formState.selectedCollateralIndex].photos.filter(
          photo => photo.id !== photoId
        )
      };

      setFormState(prev => ({
        ...prev,
        collaterals: updatedCollaterals
      }));

      toast.info('Photo removed');
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (formState.selectedCollateralIndex !== null) {
      const updatedCollaterals = [...formState.collaterals];
      updatedCollaterals[formState.selectedCollateralIndex] = {
        ...updatedCollaterals[formState.selectedCollateralIndex],
        notes: e.target.value
      };

      setFormState(prev => ({
        ...prev,
        collaterals: updatedCollaterals
      }));
    }
  };

  const handleStatusChange = async (verificationStatus: CollateralVerificationStatus) => {
    try {
      if (!formState.applicationId) {
        console.error('Cannot verify collateral: applicationId is undefined');
        toast.error('Application ID is missing. Please try refreshing the page.');
        return;
      }

      if (formState.selectedCollateralIndex === null) {
        toast.error('No collateral selected');
        return;
      }

      const selectedCollateral = formState.collaterals[formState.selectedCollateralIndex];
      
      // Get the files for verification
      const collateralReport = selectedCollateral.documents.find(doc => doc.type === DocumentType.COLLATERAL_REPORT)?.file;
      const collateralChecklist = selectedCollateral.documents.find(doc => doc.type === DocumentType.COLLATERAL_CHECKLIST)?.file;
      
      if (selectedCollateral.isVehicle && !hasVehicleRegistration) {
        toast.error('Upload the vehicle registration document before verifying this collateral');
        return;
      }

      if (selectedCollateral.photos.length === 0) {
        toast.error('At least one photo is required');
        return;
      }

      // Only include files in the verification data if they are new uploads
      const verificationData: CollateralVerification = {
        status: verificationStatus,
        notes: selectedCollateral.notes,
        assetDescription: selectedCollateral.description,
        collateralReport: collateralReport || new Blob(), // Empty blob if using existing document
        collateralChecklist: collateralChecklist || new Blob(), // Empty blob if using existing document
        photos: selectedCollateral.photos
          .filter(photo => photo.file) // Only include new photo uploads
          .map(photo => photo.file)
          .filter((file): file is File => file !== undefined)
      };

      // Set loading state
      setIsVerifyingCollateral(true);
      setOperationError(null);

      console.log('Calling verifyCollateral with:', {
        applicationId: formState.applicationId,
        data: verificationData
      });

      // Call verifyCollateral mutation
      await verifyCollateralMutation.mutateAsync({
        applicationId: formState.applicationId,
        data: verificationData
      });

      // Update local state only after successful API call
      const updatedCollaterals = [...formState.collaterals];
      updatedCollaterals[formState.selectedCollateralIndex] = {
        ...selectedCollateral,
        status: verificationStatus === CollateralVerificationStatus.APPROVE ? 'VERIFIED' : 'REJECTED',
        verificationStatus: verificationStatus
      };

      setFormState(prev => ({
        ...prev,
        collaterals: updatedCollaterals
      }));
    } catch (error: any) {
      console.error('Error verifying collateral:', error);
      setIsVerifyingCollateral(false);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      setOperationError(`Failed to verify collateral: ${errorMessage}`);
      toast.error(`Failed to verify collateral: ${errorMessage}`);
      // Scroll to top on error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Helper function to determine overall status based on all collaterals
  const determineOverallStatus = (collaterals: Collateral[]): 'PENDING' | 'VERIFIED' | 'REJECTED' => {
    if (collaterals.some(c => c.status === 'REJECTED')) {
      return 'REJECTED';
    }

    if (collaterals.every(c => c.status === 'VERIFIED')) {
      return 'VERIFIED';
    }

    return 'PENDING'; // Default if some are still pending
  };

  // Validation
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (formState.selectedCollateralIndex !== null) {
      const selectedCollateral = formState.collaterals[formState.selectedCollateralIndex];

      // Check for required documents
      const missingRequiredDocs = checkRequiredDocuments();
      if (missingRequiredDocs.length > 0) {
        errors.documents = 'Please upload all required documents';
      }

      // Check for at least one photo
      if (selectedCollateral.photos.length === 0) {
        errors.photos = 'At least one photo is required';
      }

      if (selectedCollateral.isVehicle && !hasVehicleRegistration) {
        errors.documents = 'Upload the vehicle registration document';
      }

      // Check for notes if status is REJECTED
      if (selectedCollateral.status === 'REJECTED' && !selectedCollateral.notes.trim()) {
        errors.notes = 'Notes are required when rejecting collateral';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add mutation hooks
  const verifyCollateralMutation = useVerifyCollateral({
    mutation: {
      onSuccess: () => {
        toast.success('Collateral verified successfully');
        refetchLoanApplication();
        setIsVerifyingCollateral(false);
        setOperationError(null);
      },
      onError: (error: any) => {
        setIsVerifyingCollateral(false);
        // Get more detailed error information
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        setOperationError(`Failed to verify collateral: ${errorMessage}`);
        console.error('Verification error:', error);
        toast.error(`Failed to verify collateral: ${errorMessage}`);
      }
    }
  });

  const submitCollateralVerificationMutation = useSubmitCollateralVerification({
    mutation: {
      onSuccess: () => {
        toast.success('Collateral verification submitted successfully');
        setIsSubmitted(true);
        setIsSubmitting(false);
        setOperationError(null);
      },
      onError: (error: any) => {
        setIsSubmitting(false);
        // Get more detailed error information
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        setOperationError(`Failed to submit verification: ${errorMessage}`);
        console.error('Submission error:', error);
        toast.error(`Failed to submit verification: ${errorMessage}`);
      }
    }
  });

  // Form submission
  const handleSubmit = async () => {
    // Set loading state at the beginning
    setIsSubmitting(true);
    setOperationError(null);
    
    try {
      if (!formState.applicationId) {
        setIsSubmitting(false);
        const errorMsg = 'Application ID not found';
        setOperationError(errorMsg);
        toast.error(errorMsg);
        // Scroll to top on error
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Check if all collaterals are verified
      const unverifiedCollaterals = formState.collaterals.filter(
        collateral => collateral.status === 'PENDING'
      );

      if (unverifiedCollaterals.length > 0) {
        setIsSubmitting(false);
        const errorMsg = 'All collaterals must be verified before submission';
        setOperationError(errorMsg);
        toast.error(errorMsg);
        // Scroll to top on error
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Determine overall status based on individual collateral statuses
      const hasRejectedCollaterals = formState.collaterals.some(
        collateral => collateral.status === 'REJECTED'
      );

      const submissionData: CollateralVerificationSubmission = {
        status: hasRejectedCollaterals ? CollateralVerificationStatus.REJECT : CollateralVerificationStatus.APPROVE,
        notes: formState.notes,
        recordedBy: user?.name || user?.username
      };

      console.log('Submitting verification with data:', submissionData);

      // Submit verification
      await submitCollateralVerificationMutation.mutateAsync({
        applicationId: formState.applicationId,
        data: submissionData
      });

      // Success handling is done in the mutation's onSuccess callback
    } catch (error: any) {
      console.error('Error submitting verification:', error);
      setIsSubmitting(false);
      
      // Extract detailed error information
      const errorDetails = error?.response?.data?.message || error?.message || 'Unknown error';
      const errorMsg = `Failed to submit verification: ${errorDetails}`;
      
      setOperationError(errorMsg);
      toast.error(errorMsg);
      // Scroll to top on error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Rendering
  const selectedCollateral = formState.selectedCollateralIndex !== null
    ? formState.collaterals[formState.selectedCollateralIndex]
    : null;

  const handleCancelApplication = async () => {
      if (!cancelReason.trim()) {
          toast.error('Please provide a reason for cancellation');
          return;
      }
      const appId = loanApplicationId;
      if (!appId) {
          toast.error('No application ID available');
          return;
      }
      setIsCancelling(true);
      try {
          await cancelLoanApplication(appId, { reason: cancelReason.trim() });
          toast.success('Application cancelled successfully');
          setShowCancelModal(false);
          setCancelReason('');
          navigate('/applications');
      } catch (err: any) {
          const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
          toast.error(message);
      } finally {
          setIsCancelling(false);
      }
  };

  // Improved renderDocumentCard function based on DocumentUploadStep
  const renderDocumentCard = (title: string, description: string, docType: string, required: boolean = false) => {
    if (!selectedCollateral) return null;

    const isUploaded = selectedCollateral.documents.some(doc => doc.type === docType);
    const isMissing = missingDocTypes.includes(docType);

    return (
      <div className="required-document-card mb-3">
        <div className={`card ${isUploaded ? 'uploaded' : ''} ${isMissing ? 'missing' : ''}`}>
          <div className="card-body">
            <div className="document-info">
              <h6 className="document-title">
                {title} {required && <span className="text-danger">*</span>}
              </h6>
              <p className="document-description">{description}</p>
              {isMissing && (
                <p className="text-danger mb-0"><small>This document is required</small></p>
              )}
            </div>
            <div className="document-upload">
              {isUploaded && (
                <div className="document-name mb-2">
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    {selectedCollateral.documents.find(d => d.type === docType)?.fileName}
                  </small>
                </div>
              )}
              <div className="d-flex gap-2">
                <input
                  type="file"
                  id={`file-${docType}`}
                  className="d-none"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange(e, docType)}
                  disabled={!!uploadProgress}
                />
                <label
                  htmlFor={`file-${docType}`}
                  className={`btn ${isUploaded ? 'btn-success' : 'btn-outline-primary'} ${isMissing ? 'btn-missing' : ''}`}
                >
                  <i className={`bi ${isUploaded ? 'bi-check' : 'bi-upload'} me-2`}></i>
                  {isUploaded ? 'Replace File' : 'Upload File'}
                </label>
                {isUploaded && (
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => {
                        const doc = selectedCollateral.documents.find(d => d.type === docType);
                        if (doc?.fileUrl) window.open(doc.fileUrl, '_blank');
                      }}
                    >
                      <i className="bi bi-eye me-2"></i>
                      View
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => {
                        const doc = selectedCollateral.documents.find(d => d.type === docType);
                        if (doc) handleDeleteDocument(doc.id);
                      }}
                      disabled={!!uploadProgress}
                    >
                      <i className="bi bi-trash me-2"></i>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isSubmitted) {
    return (
      <div className="container-fluid p-4">
        <div className="card shadow-sm border-0">
          <div className="card-body text-center py-5">
            <div className="success-icon-container mb-4">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '5rem' }}></i>
              <div className="success-rays"></div>
            </div>
            <h2 className="mb-3 fw-bold">Collateral Verification Complete!</h2>
            <p className="text-muted mb-4 lead">Your verification has been recorded and the application has moved on to its next stage.</p>
            
            <div className="success-details p-3 mb-4 bg-light rounded-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="success-detail-icon me-3 bg-primary bg-opacity-10 p-2 rounded-circle">
                      <i className="bi bi-calendar-check text-primary"></i>
                    </div>
                    <div className="text-start">
                      <small className="text-muted d-block">Submission Date</small>
                      <span>{new Date().toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="success-detail-icon me-3 bg-success bg-opacity-10 p-2 rounded-circle">
                      <i className="bi bi-person-check text-success"></i>
                    </div>
                    <div className="text-start">
                      <small className="text-muted d-block">Verified By</small>
                      <span>You</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="d-flex justify-content-center gap-3">
              <button
                className="btn btn-primary px-4 py-2"
                onClick={() => navigate('/work-items')}
              >
                <i className="bi bi-list-check me-2"></i>
                Return to Work Items
              </button>
              <button
                className="btn btn-outline-secondary px-4 py-2"
                onClick={() => navigate('/loan-processing')}
              >
                <i className="bi bi-grid me-2"></i>
                Loan Processing Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && !loanApplication) {
    return (
      <div className="container-fluid py-4">
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="mb-3">Loading Application Data</h5>
            <p className="text-muted mb-0">Please wait while we fetch the collateral verification details...</p>
            <div className="progress mt-4" style={{ height: '8px' }}>
              <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-4">
        <div className="card">
          <div className="card-body">
            <div className="text-center mb-4">
              <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3 text-danger">Error Loading Data</h4>
            </div>
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
            </div>
            <div className="mt-4 d-flex justify-content-center gap-3">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  refetchLoanApplication();
                }}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Retry Loading Data
              </button>
              <Link to="/collateral-verification" className="btn btn-outline-secondary">
                <i className="bi bi-arrow-left me-2"></i>
                Back to Collateral Verification
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4 align-items-center">
        <div className="col">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb bg-transparent mb-0">
              <li className="breadcrumb-item"><Link to="/" className="text-decoration-none">Home</Link></li>
              <li className="breadcrumb-item"><Link to="/collateral-verification" className="text-decoration-none">Collateral Verification</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Details</li>
            </ol>
          </nav>
          <h4 className="mt-2 mb-0 text-dark">Collateral Verification</h4>
        </div>
        <div className="col-auto">
          <Link to="/collateral-verification" className="btn btn-outline-secondary">
            <i className="bi bi-arrow-left me-2"></i>Back to List
          </Link>
        </div>
      </div>

      {/* Global Error Alert */}
      {operationError && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill fs-4 me-2"></i>
                <div>
                  <strong>Error!</strong> {operationError}
                </div>
              </div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setOperationError(null)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )}

      {loanApplication && (
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title d-flex align-items-center mb-3">
                  <i className="bi bi-info-circle me-2 text-primary"></i>
                  Application Details
                </h5>
                <div className="application-details-container">
                  <div className="application-detail-item border-bottom pb-2 mb-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted d-block">Application Number</small>
                        <span className="fw-semibold">{loanApplication.applicationNumber}</span>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Application Status</small>
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill">
                          {loanApplication.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="application-detail-item border-bottom pb-2 mb-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <small className="text-muted d-block">Customer</small>
                        <div className="d-flex align-items-center">
                          <i className="bi bi-person-circle me-2 text-primary-subtle"></i>
                          <span>{loanApplication.customerNumber || loanApplication.individual?.fullNames || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Loan Type</small>
                        <div className="d-flex align-items-center justify-content-end">
                          <i className="bi bi-tag me-2 text-primary-subtle"></i>
                          <span>{loanApplication.loanType}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="application-detail-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <small className="text-muted d-block">Loan Amount</small>
                        <div className="amount-container d-inline-flex align-items-center px-2 py-1 bg-success-subtle rounded">
                          <i className="bi bi-cash-stack me-2 text-success"></i>
                          <span className="fw-bold text-success">
                            {new Intl.NumberFormat('en-ZA', {
                              style: 'currency',
                              currency: 'ZAR'
                            }).format(loanApplication.loanDetails?.loanAmount || 0)}
                          </span>
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Loan Term</small>
                        <div className="d-flex align-items-center justify-content-end">
                          <i className="bi bi-calendar-event me-2 text-primary-subtle"></i>
                          <span>{loanApplication.loanDetails?.loanTerms || 'N/A'} months</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title d-flex align-items-center mb-3">
                  <i className="bi bi-shield-check me-2 text-primary"></i>
                  Collateral Summary
                </h5>
                {formState.collaterals.length > 0 ? (
                  <div className="collateral-summary-cards">
                    {formState.collaterals.map((collateral, index) => (
                      <div
                        key={index}
                        className={`collateral-card-elegant mb-3 ${formState.selectedCollateralIndex === index ? 'selected' : ''}`}
                        onClick={() => {
                          setFormState(prev => ({
                            ...prev,
                            selectedCollateralIndex: index
                          }));
                        }}
                      >
                        <div className="card-content-wrapper">
                          <div className="left-section">
                            <div className={`status-pill status-${collateral.status.toLowerCase()}`}>
                              <span className="status-text">{collateral.status}</span>
                            </div>
                            <h6 className="type-title">{collateral.type}</h6>
                            <p className="description-text">{collateral.description}</p>
                            <div className="meta-details">
                              <span className="meta-item">
                                <i className="bi bi-file-earmark me-1"></i>
                                {collateral.documents.length} docs
                              </span>
                              <span className="meta-item">
                                <i className="bi bi-camera me-1"></i>
                                {collateral.photos.length}/{MAX_PHOTOS} photos
                              </span>
                              {collateral.isVehicle && (
                                <span className={`meta-item ${collateral.insured ? '' : 'text-danger'}`}>
                                  <i className="bi bi-shield-check me-1"></i>
                                  {collateral.insured
                                    ? `Insured${collateral.insurer ? ` with ${collateral.insurer}` : ''}${collateral.insurancePolicyNumber ? ` (${collateral.insurancePolicyNumber})` : ''}`
                                    : collateral.insured === false ? 'Not insured' : 'Insurance not recorded'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="right-section">
                            <div className="value-display">
                              {new Intl.NumberFormat('en-ZA', {
                                style: 'currency',
                                currency: 'ZAR'
                              }).format(collateral.value)}
                            </div>
                            {formState.selectedCollateralIndex === index && (
                              <div className="selection-marker">
                                <i className="bi bi-check-circle-fill"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-info d-flex align-items-center">
                    <i className="bi bi-info-circle-fill me-2"></i>
                    <div>No collateral items found for this application.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header bg-white py-3">
          <h5 className="card-title mb-0">
            <i className="bi bi-clipboard-check me-2 text-primary"></i>
            Collateral Verification Details
          </h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="collateral-select" className="form-label fw-semibold">Select Collateral</label>
              <select
                id="collateral-select"
                className="form-select form-select-lg"
                value={formState.selectedCollateralIndex !== null ? formState.selectedCollateralIndex : ''}
                onChange={handleCollateralChange}
              >
                {formState.collaterals.map((collateral, index) => (
                  <option key={index} value={index}>
                    {collateral.type} - {collateral.description}
                  </option>
                ))}
              </select>
            </div>

            {selectedCollateral && (
              <>
                <div className="document-upload-section">
                  <div className="section-header d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                      Security Documents
                    </h5>
                    {uploadProgress !== null && (
                      <div className="upload-progress d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="small">Uploading... {uploadProgress}%</span>
                      </div>
                    )}
                  </div>

                  <div className="alert alert-info mb-4">
                    <div className="d-flex">
                      <div className="me-3">
                        <i className="bi bi-info-circle fs-4"></i>
                      </div>
                      <div>
                        <p className="mb-0">
                          <strong>Security documents:</strong> these are the same documents as on the Upload Documents page, so anything uploaded there already shows here. PDF, JPG, PNG formats only. Maximum file size: 100MB per document.
                        </p>
                      </div>
                    </div>
                  </div>

                  {uploadError && (
                    <div className="alert alert-danger mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <span><i className="bi bi-exclamation-triangle me-2"></i>{uploadError}</span>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setUploadError(null)}
                          aria-label="Close"
                        ></button>
                      </div>
                    </div>
                  )}

                  {validationErrors.documents && (
                    <div className="alert alert-danger d-flex align-items-center mt-3" role="alert">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <div>{validationErrors.documents}</div>
                    </div>
                  )}

                  <div className="row">
                    {getRequiredDocuments()
                      .map((doc) => (
                        <div key={doc.type} className="col-md-6">
                          {renderDocumentCard(doc.label, doc.description, doc.type, doc.required)}
                        </div>
                      ))}
                  </div>
                  {formState.applicationId && (
                    <CollateralVehicleDocuments
                      applicationId={formState.applicationId}
                      registrationRequired={!!selectedCollateral?.isVehicle}
                      isVehicle={!!selectedCollateral?.isVehicle}
                      onRegistrationChange={setHasVehicleRegistration}
                    />
                  )}
                </div>

                <div className="document-upload-section mt-5">
                  <div className="section-header d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <i className="bi bi-camera me-2 text-primary"></i>
                      Collateral Photos
                    </h5>
                  </div>

                  <div className="alert alert-info d-flex align-items-center mb-4">
                    <i className="bi bi-info-circle-fill me-2"></i>
                    <div>Upload clear photos of the collateral from different angles. At least 1 and up to {MAX_PHOTOS} photos. JPG, PNG formats only. Maximum 100MB per photo.</div>
                  </div>

                  {uploadError && (
                    <div className="alert alert-danger mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <span><i className="bi bi-exclamation-triangle me-2"></i>{uploadError}</span>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setUploadError(null)}
                          aria-label="Close"
                        ></button>
                      </div>
                    </div>
                  )}

                  {validationErrors.photos && (
                    <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <div>{validationErrors.photos}</div>
                    </div>
                  )}

                  <div className="card mb-4">
                    <div className="card-body">
                      <div className="photo-upload-container">
                        <input
                          type="file"
                          id="photo-upload"
                          className="photo-input"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoUpload}
                          style={{ display: 'none' }}
                        />
                        <label
                          htmlFor="photo-upload"
                          className={`photo-upload-area ${isDraggingPhoto ? 'active-drop' : ''}`}
                          onDragOver={(e: React.DragEvent<HTMLLabelElement>) => {
                            e.preventDefault();
                            setIsDraggingPhoto(true);
                          }}
                          onDragLeave={(e: React.DragEvent<HTMLLabelElement>) => {
                            e.preventDefault();
                            setIsDraggingPhoto(false);
                          }}
                          onDrop={(e: React.DragEvent<HTMLLabelElement>) => {
                            e.preventDefault();
                            setIsDraggingPhoto(false);

                            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                              handlePhotoUpload({ target: { files: e.dataTransfer.files } } as React.ChangeEvent<HTMLInputElement>);
                            }
                          }}
                        >
                          <div className="text-center p-5">
                            <i className="bi bi-camera-fill fs-1 text-primary mb-3"></i>
                            <h6>Drag & Drop Photos Here</h6>
                            <p className="text-muted mb-3">Or click to browse from your device</p>
                            <button type="button" className="btn btn-primary px-4" onClick={() => document.getElementById('photo-upload')?.click()}>
                              <i className="bi bi-upload me-2"></i>Upload Photos
                            </button>
                            <p className="text-muted small mt-3 mb-0">
                              <i className="bi bi-info-circle me-1"></i>
                              JPG, PNG formats only. Maximum 100MB per photo.
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {selectedCollateral.photos.length > 0 && (
                    <div className="card">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                        <h6 className="mb-0">
                          <i className="bi bi-images me-2"></i>
                          Uploaded Photos
                        </h6>
                        <div>
                          <span className="badge bg-primary rounded-pill">{selectedCollateral.photos.length} of {MAX_PHOTOS} Photos</span>
                          {selectedCollateral.photos.length >= MAX_PHOTOS && (
                            <span className="ms-2 badge bg-warning text-dark">Maximum reached</span>
                          )}
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          {selectedCollateral.photos.map(photo => (
                            <div key={photo.id} className="col-6 col-sm-4 col-md-3 col-lg-2">
                              <div className="photo-item">
                                <div className="photo-preview" onClick={() => handlePreviewPhoto({
                                  id: photo.id,
                                  fileName: photo.fileName,
                                  uploadDate: photo.uploadDate,
                                  imageUrl: photo.imageUrl,
                                  file: photo.file,
                                  isExisting: photo.isExisting
                                })}>
                                  <div className="position-relative">
                                    <img
                                      src={photo.imageUrl}
                                      alt={photo.fileName}
                                      className="img-fluid rounded"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgZmlsbD0iY3VycmVudENvbG9yIiBjbGFzcz0iYmkgYmktaW1hZ2UiIHZpZXdCb3g9IjAgMCAxNiAxNiI+CiAgPHBhdGggZD0iTTYuMDAyIDUuNWExLjUgMS41IDAgMSAxLTMgMCAxLjUgMS41IDAgMCAxIDMgMHoiLz4KICA8cGF0aCBkPSJNMi4wMDIgMWEyIDIgMCAwIDAtMiAydjEwYTIgMiAwIDAgMCAyIDJoMTJhMiAyIDAgMCAwIDItMlYzYTIgMiAwIDAgMC0yLTJoLTEyem0xMiAxYTEgMSAwIDAgMSAxIDF2Ni41bC0zLjc3Ny0xLjk0N2EuNS41IDAgMCAwLS41NzcuMDkzbC0zLjcxIDMuNzEtMi42Ni0xLjc3MmEuNS41IDAgMCAwLS42My4wNjJMMS4wMDIgMTJWM2ExIDEgMCAwIDEgMS0xaDEyeiIvPgo8L3N2Zz4=';
                                        target.classList.add('placeholder-img');
                                      }}
                                    />
                                    {photo.isExisting && (
                                      <span className="position-absolute top-0 end-0 badge bg-info m-2" title="Previously uploaded photo">
                                        <i className="bi bi-check-circle-fill"></i>
                                      </span>
                                    )}
                                  </div>
                                  <div className="photo-overlay">
                                    <button type="button" className="btn btn-sm btn-light" title="View Larger">
                                      <i className="bi bi-eye-fill"></i>
                                    </button>
                                  </div>
                                </div>
                                <div className="photo-actions mt-2 d-flex justify-content-between align-items-center">
                                  <small className="text-muted text-truncate" style={{ maxWidth: "70%" }}>
                                    {photo.fileName}
                                  </small>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeletePhoto(photo.id)}
                                    title="Delete Photo"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="col-6 col-sm-4 col-md-3 col-lg-2">
                            <div
                              className={`add-more-photos ${selectedCollateral.photos.length >= MAX_PHOTOS ? 'disabled' : ''}`}
                              onClick={() => {
                                if (selectedCollateral.photos.length < MAX_PHOTOS) {
                                  document.getElementById('photo-upload')?.click();
                                } else {
                                  toast.warning(`Maximum ${MAX_PHOTOS} photos allowed`);
                                }
                              }}
                            >
                              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                <i className={`bi ${selectedCollateral.photos.length >= MAX_PHOTOS ? 'bi-x-circle' : 'bi-plus-circle'} fs-1 ${selectedCollateral.photos.length >= MAX_PHOTOS ? 'text-muted' : 'text-primary'} mb-2`}></i>
                                <span className="text-muted small">{selectedCollateral.photos.length >= MAX_PHOTOS ? 'Maximum Reached' : 'Add More'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="document-upload-section">
                  <h6 className="fw-bold mb-3">
                    <i className="bi bi-pencil-square me-2"></i>
                    Verification Notes
                  </h6>

                  <div className="form-floating mb-3">
                    <textarea
                      id="notes"
                      className={`form-control ${validationErrors.notes ? 'is-invalid' : ''}`}
                      placeholder="Add notes about the collateral verification"
                      style={{ height: '120px' }}
                      value={selectedCollateral.notes}
                      onChange={handleNotesChange}
                    ></textarea>
                    <label htmlFor="notes">Add notes about the collateral verification</label>
                    {validationErrors.notes && (
                      <div className="invalid-feedback">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {validationErrors.notes}
                      </div>
                    )}
                  </div>
                </div>

                <div className="document-upload-section mt-4">
                  <div className="card verification-status-card">
                    <div className="card-header bg-light">
                      <h5 className="mb-0 d-flex align-items-center">
                        <i className="bi bi-shield-check me-2 text-primary"></i>
                        Collateral Verification Decision
                      </h5>
                    </div>
                    <div className="card-body p-4">
                      <div className="alert alert-info">
                        <i className="bi bi-info-circle me-2"></i>
                        Verify this collateral by approving or rejecting it. You must verify all collaterals before submitting the final verification.
                      </div>

                      <div className="d-flex gap-3 justify-content-center mt-4">
                        <button
                          type="button"
                          className={`btn btn-lg ${selectedCollateral.status === 'VERIFIED' ? 'btn-success' : 'btn-outline-success'} flex-grow-1`}
                          onClick={() => handleStatusChange(CollateralVerificationStatus.APPROVE)}
                          disabled={!selectedCollateral.documents.length || selectedCollateral.photos.length === 0 || isVerifyingCollateral}
                        >
                          <div className="d-flex flex-column align-items-center">
                            {isVerifyingCollateral ? (
                              <>
                                <span className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true"></span>
                                <span className="fw-bold">Verifying...</span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-check-circle-fill fs-3 mb-2"></i>
                                <span className="fw-bold">Approve Collateral</span>
                                <small className="text-muted">Verify this collateral item</small>
                              </>
                            )}
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`btn btn-lg ${selectedCollateral.status === 'REJECTED' ? 'btn-danger' : 'btn-outline-danger'} flex-grow-1`}
                          onClick={() => handleStatusChange(CollateralVerificationStatus.REJECT)}
                          disabled={!selectedCollateral.documents.length || selectedCollateral.photos.length === 0 || isVerifyingCollateral}
                        >
                          <div className="d-flex flex-column align-items-center">
                            {isVerifyingCollateral ? (
                              <>
                                <span className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true"></span>
                                <span className="fw-bold">Verifying...</span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-x-circle-fill fs-3 mb-2"></i>
                                <span className="fw-bold">Reject Collateral</span>
                                <small className="text-muted">Mark this collateral as rejected</small>
                              </>
                            )}
                          </div>
                        </button>
                      </div>

                      {isVerifyingCollateral && (
                        <div className="alert alert-info mt-3 mb-0 d-flex align-items-center">
                          <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                          <div>Verifying collateral... Please wait.</div>
                        </div>
                      )}

                      {operationError && operationError.includes('verify collateral') && (
                        <div className="alert alert-danger mt-3 mb-0">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>{operationError}</div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger mt-2"
                            onClick={() => setOperationError(null)}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}

                      {(selectedCollateral.documents.length === 0 || selectedCollateral.photos.length === 0) && (
                        <div className="alert alert-warning mt-3 mb-0">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Upload required documents and photos before verifying this collateral.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Final Verification Submission Section */}
                <div className="mt-4">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h5 className="mb-0 d-flex align-items-center">
                        <i className="bi bi-send-check me-2 text-primary"></i>
                        Submit Final Verification
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="verification-progress mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-bold">Verification Progress</span>
                          <span className="badge bg-primary">
                            {formState.collaterals.filter(c => c.status !== 'PENDING').length} of {formState.collaterals.length} Verified
                          </span>
                        </div>
                        <div className="progress" style={{ height: '10px' }}>
                          <div
                            className="progress-bar bg-success"
                            role="progressbar"
                            style={{
                              width: `${(formState.collaterals.filter(c => c.status !== 'PENDING').length / formState.collaterals.length) * 100}%`
                            }}
                          ></div>
                        </div>
                      </div>

                      {formState.collaterals.some(c => c.status === 'PENDING') ? (
                        <div className="alert alert-warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Please verify all collaterals before submitting the final verification.
                        </div>
                      ) : (
                        <>
                          <div className="alert alert-success mb-4">
                            <i className="bi bi-check-circle me-2"></i>
                            All collaterals have been verified. You can now submit the final verification.
                          </div>

                          <div className="form-group mb-4">
                            <label className="form-label fw-bold">Final Verification Notes</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              value={formState.notes}
                              onChange={(e) => setFormState(prev => ({ ...prev, notes: e.target.value }))}
                              placeholder="Add any final notes about the overall collateral verification..."
                            ></textarea>
                          </div>

                          <div className="d-flex gap-3 justify-content-center">
                            <button
                              type="submit"
                              className="btn btn-lg btn-success flex-grow-1"
                              onClick={(e) => {
                                e.preventDefault();
                                setFormState(prev => ({ ...prev, status: 'VERIFIED' }));
                                handleSubmit();
                              }}
                              disabled={isLoading || isSubmitting || isSubmitted}
                            >
                              <div className="d-flex flex-column align-items-center">
                                {isSubmitting ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true"></span>
                                    <span className="fw-bold">Submitting...</span>
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-check-circle-fill fs-3 mb-2"></i>
                                    <span className="fw-bold">Approve & Submit</span>
                                    <small>Submit final approval</small>
                                  </>
                                )}
                              </div>
                            </button>

                            <button
                              type="submit"
                              className="btn btn-lg btn-danger flex-grow-1"
                              onClick={(e) => {
                                e.preventDefault();
                                setFormState(prev => ({ ...prev, status: 'REJECTED' }));
                                handleSubmit();
                              }}
                              disabled={isLoading || isSubmitting || isSubmitted || !formState.notes.trim()}
                            >
                              <div className="d-flex flex-column align-items-center">
                                {isSubmitting ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true"></span>
                                    <span className="fw-bold">Submitting...</span>
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-x-circle-fill fs-3 mb-2"></i>
                                    <span className="fw-bold">Reject & Submit</span>
                                    <small>Submit final rejection</small>
                                  </>
                                )}
                              </div>
                            </button>
                          </div>

                          {/* Inline error message for submission errors */}
                          {operationError && operationError.includes('submit verification') && (
                            <div className="alert alert-danger mt-3">
                              <div className="d-flex align-items-center">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <div>{operationError}</div>
                              </div>
                              <div className="mt-2 d-flex justify-content-between align-items-center">
                                <small>You can try again or check your network connection</small>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => setOperationError(null)}
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          )}

                          {!formState.notes.trim() && (
                            <div className="alert alert-warning mt-3 mb-0">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              Please provide notes before rejecting the verification.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="form-actions mt-4">
              <button
                  className="btn btn-outline-danger ms-2"
                  onClick={() => setShowCancelModal(true)}
              >
                  <i className="fa fa-times-circle me-1"></i> Cancel Application
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Cancel Application Modal */}
      {showCancelModal && (
          <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
              <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                      <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                          <div className="d-flex align-items-center">
                              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                                  <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                              </div>
                              <div>
                                  <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                                  <p className="text-muted mb-0 small">This action cannot be undone</p>
                              </div>
                          </div>
                          <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
                      </div>
                      <div className="modal-body p-4">
                          <p className="text-muted mb-3">Are you sure you want to cancel this loan application? Please provide a reason:</p>
                          <textarea
                              className="form-control"
                              rows={3}
                              value={cancelReason}
                              onChange={(e) => setCancelReason(e.target.value)}
                              placeholder="Enter reason for cancellation..."
                              disabled={isCancelling}
                              maxLength={500}
                          />
                          <small className="text-muted">{cancelReason.length}/500 characters</small>
                      </div>
                      <div className="modal-footer border-0 px-4 pb-4">
                          <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                              Close
                          </button>
                          <button
                              type="button"
                              className="btn btn-danger rounded-pill px-4"
                              onClick={handleCancelApplication}
                              disabled={isCancelling || !cancelReason.trim()}
                          >
                              {isCancelling ? (
                                  <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Cancelling...</>
                              ) : (
                                  <><i className="fa fa-times-circle me-2"></i>Cancel Application</>
                              )}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default CollateralVerificationDetails;
